'use client';

import { usePathname, useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import type { PaywallAction } from '@/shared/lib/api';
import { PaywallBullets } from '@/features/payment/ui/PaywallBullets';

/**
 * Root-mounted dialog that renders whatever PaywallPayload the API put in
 * the current limit error. Everything (title, body, button labels) comes
 * from the server — no local copy. See apps/api/app/Dtos/PaywallPayload.php.
 *
 * `secondary_action.target` is a semantic route token (e.g. `project_angles`)
 * resolved to a concrete URL here. The button is hidden when the user is
 * already on the target screen so it doesn't duplicate Cancel.
 */
export function LimitPaywallDialog() {
  const router = useRouter();
  const pathname = usePathname();
  const payload = useLimitPaywallStore((s) => s.payload);
  const hidePaywall = useLimitPaywallStore((s) => s.hidePaywall);
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const projectId = pathname?.match(/^\/project\/([^/]+)/)?.[1];

  const targetPath = (target: string | null): string | null => {
    if (!target) return null;
    switch (target) {
      case 'projects':
        return '/';
      case 'project_angles':
        return projectId ? `/project/${projectId}/angles` : null;
      case 'project_references':
        return projectId ? `/project/${projectId}/references` : null;
      default:
        return null;
    }
  };

  const handleAction = (action: PaywallAction) => {
    hidePaywall();
    switch (action.kind) {
      case 'purchase_tokens':
        router.push('/account/tokens');
        break;
      case 'open_pass_paywall':
        // Monetization-pivot Pass upsell for a Pass-liftable limit. Server
        // `target` is the entitlement key ("pro_pass" / "designer_pass"); the
        // store highlight uses the short form ("pro" / "designer").
        openPassPaywall(action.target === 'designer_pass' ? 'designer' : 'pro');
        break;
      case 'delete_existing': {
        const path = targetPath(action.target);
        if (path) router.push(path);
        break;
      }
      case 'dismiss':
        break;
    }
  };

  // Hide the secondary action only when it would re-route to the screen the
  // user is already on (mostly `delete_existing` with a target). For
  // `purchase_tokens` we always show it — the destination is implicit and the
  // user came here specifically to act on it.
  const showSecondary = (() => {
    const action = payload?.secondary_action;
    if (!action) return false;
    if (action.kind === 'delete_existing') {
      const path = targetPath(action.target);
      if (!path) return false;
      return pathname !== path;
    }
    return true;
  })();

  return (
    <Dialog
      open={payload !== null}
      onOpenChange={(open) => {
        if (!open) hidePaywall();
      }}
    >
      <DialogContent showCloseButton={false} className="max-w-[460px]">
        {payload && (
          <>
            <DialogHeader>
              <DialogTitle>{payload.title}</DialogTitle>
              {/* `max-w-[80%]` caps the body at 80% of the dialog content
                  width so the paragraph balances and the last line doesn't
                  strand a single orphan word. DialogDescription is already
                  left-aligned by default — mirrors Apple/Android.
                  `text-card-body` overrides the default `text-form-help` so
                  the subtitle matches the bullet text size (both 16px), in
                  parity with Apple/Android (both `cardBody`). */}
              <DialogDescription className="text-card-body max-w-[80%]">
                {payload.body}
              </DialogDescription>
            </DialogHeader>
            {/* Server-driven benefit bullets (e.g. INSUFFICIENT_TOKENS carries
                3) — rendered above the actions; absent on most payloads.
                `mt-6` gives the value-prop block a clear visual break under
                the title+subtitle header (which sit tight together) — calmer
                rhythm than uniform spacing through the body. */}
            <PaywallBullets bullets={payload.bullets} className="mt-6" />
            {payload.primary_action ? (
              <DialogFooter className="gap-2">
                <Button
                  variant="ghost"
                  onClick={() => handleAction(payload.dismiss_action)}
                >
                  {payload.dismiss_action.label}
                </Button>
                {showSecondary && payload.secondary_action && (
                  <Button
                    variant="outline"
                    onClick={() => handleAction(payload.secondary_action!)}
                  >
                    {payload.secondary_action.label}
                  </Button>
                )}
                <Button onClick={() => handleAction(payload.primary_action!)}>
                  {payload.primary_action.label}
                </Button>
              </DialogFooter>
            ) : (
              // Info-only paywall: the limit can't be relieved by an action
              // here (daily/monthly render caps, or already on the top Pass).
              // The single dismiss is styled as the affirmative, full-width
              // primary button — Apple-HIG single-action sheet. No CTA.
              <DialogFooter>
                <Button
                  className="w-full"
                  onClick={() => handleAction(payload.dismiss_action)}
                >
                  {payload.dismiss_action.label}
                </Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
